import { buildPublicIntelHtml } from './publicAccountIntel';
import { getDbSupabase, isSupabaseConfigured } from './supabase';
import type { AccountIntelRegistry, Client } from '../types/crm';

export interface AccountIntelResult {
  html: string;
  newsHtml: string;
  registry: AccountIntelRegistry | null;
  registryError: string | null;
  cachedAt?: string;
  source?: string;
  suggestedUen?: string | null;
}

function asRegistry(raw: unknown): AccountIntelRegistry | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  return {
    uen: typeof row.uen === 'string' ? row.uen : null,
    legalName: typeof row.legalName === 'string' ? row.legalName : null,
    status: typeof row.status === 'string' ? row.status : null,
    entityType: typeof row.entityType === 'string' ? row.entityType : null,
    registrationDate: typeof row.registrationDate === 'string' ? row.registrationDate : null,
    registeredAddress: typeof row.registeredAddress === 'string' ? row.registeredAddress : null,
    primarySsic: typeof row.primarySsic === 'string' ? row.primarySsic : null,
    paidUpCapital: typeof row.paidUpCapital === 'string' ? row.paidUpCapital : null,
    currency: typeof row.currency === 'string' ? row.currency : null,
    source: typeof row.source === 'string' ? row.source : undefined,
    fetchedAt: typeof row.fetchedAt === 'string' ? row.fetchedAt : undefined,
    error: typeof row.error === 'string' ? row.error : null,
  };
}

async function saveIntelCache(
  clientId: string,
  newsHtml: string,
  registry: AccountIntelRegistry | null,
): Promise<string> {
  const cachedAt = new Date().toISOString();
  const { error } = await getDbSupabase()
    .from('clients')
    .update({
      intel_html: newsHtml,
      intel_updated_at: cachedAt,
      intel_profile_json: registry,
    })
    .eq('id', clientId);

  if (error && !/intel_html|intel_profile_json|column/i.test(error.message ?? '')) {
    throw error;
  }
  return cachedAt;
}

async function fetchEdgeIntel(
  client: Pick<Client, 'id' | 'company_name' | 'website' | 'uen'>,
): Promise<{
  newsHtml: string;
  registry: AccountIntelRegistry | null;
  registryError: string | null;
  source?: string;
  suggestedUen?: string | null;
} | null> {
  if (!isSupabaseConfigured) return null;

  const { data, error } = await getDbSupabase().functions.invoke('account-intel', {
    body: {
      companyName: client.company_name,
      website: client.website ?? '',
      uen: client.uen ?? '',
      clientId: client.id,
    },
  });

  if (error) {
    console.warn('[account-intel]', error.message);
    return null;
  }

  const payload = data as {
    html?: string;
    newsHtml?: string;
    registry?: AccountIntelRegistry | null;
    registry_error?: string;
    error?: string;
    source?: string;
    suggestedUen?: string | null;
  } | null;

  if (payload?.error && !payload.newsHtml && !payload.html && !payload.registry) {
    throw new Error(payload.error);
  }

  const newsHtml = (payload?.newsHtml ?? payload?.html ?? '').trim();
  return {
    newsHtml,
    registry: payload?.registry ?? null,
    registryError: payload?.registry_error ?? payload?.registry?.error ?? null,
    source: payload?.source,
    suggestedUen: payload?.suggestedUen ?? null,
  };
}

/** Hybrid intelligence: ACRA registry + public-web news. */
export async function fetchAccountIntel(
  client: Pick<Client, 'id' | 'company_name' | 'website' | 'uen'>,
): Promise<AccountIntelResult> {
  const edge = await fetchEdgeIntel(client);

  let newsHtml = '';
  let registry: AccountIntelRegistry | null = null;
  let registryError: string | null = null;
  let source: string | undefined;
  let suggestedUen: string | null = null;

  if (edge) {
    newsHtml = edge.newsHtml;
    registry = edge.registry;
    registryError = edge.registryError;
    source = edge.source ?? 'edge';
    suggestedUen = edge.suggestedUen ?? null;
  }

  if (!newsHtml) {
    newsHtml = await buildPublicIntelHtml(client.company_name, client.website);
    source = source ?? 'public';
  }

  if (!newsHtml.includes('account-intel-hit') && !registry) {
    throw new Error(
      registryError ||
        'No intelligence found. Add UEN / website, configure ACRA + GEMINI_API_KEY, or use Google News below.',
    );
  }

  const cachedAt = await saveIntelCache(client.id, newsHtml, registry).catch(() =>
    new Date().toISOString(),
  );

  return {
    html: newsHtml,
    newsHtml,
    registry,
    registryError,
    cachedAt,
    source,
    suggestedUen,
  };
}

/** Registry lookup only (ACRA / data.gov.sg) — no news crawl. */
export async function fetchRegistryIntel(
  companyName: string,
  uen = '',
): Promise<{ registry: AccountIntelRegistry | null; error: string | null; source?: string }> {
  if (!isSupabaseConfigured) {
    return { registry: null, error: 'Supabase is not configured.' };
  }

  const { data, error } = await getDbSupabase().functions.invoke('account-intel', {
    body: {
      companyName: companyName.trim(),
      uen: uen.trim(),
      registryOnly: true,
    },
  });

  if (error) {
    return { registry: null, error: error.message };
  }

  const payload = data as {
    registry?: unknown;
    registry_error?: string | null;
    source?: string;
    error?: string;
  };

  if (payload?.error) {
    return { registry: null, error: payload.error };
  }

  return {
    registry: asRegistry(payload?.registry),
    error: payload?.registry_error ?? null,
    source: payload?.source,
  };
}

export async function loadCachedAccountIntel(clientId: string): Promise<AccountIntelResult | null> {
  const { data, error } = await getDbSupabase()
    .from('clients')
    .select('intel_html, intel_updated_at, intel_profile_json')
    .eq('id', clientId)
    .maybeSingle();

  if (error) {
    if (/intel_html|intel_profile|column|schema|PGRST204/i.test(error.message ?? '')) return null;
    return null;
  }
  if (!data) return null;

  const html = typeof data.intel_html === 'string' ? data.intel_html : '';
  const registry = asRegistry(data.intel_profile_json);
  if (!html && !registry) return null;
  if (html.includes('Your CRM snapshot') || html.includes('CRM description')) {
    return registry
      ? {
          html: '',
          newsHtml: '',
          registry,
          registryError: registry.error ?? null,
          cachedAt: (data.intel_updated_at as string | undefined) ?? undefined,
          source: 'cache',
        }
      : null;
  }

  return {
    html,
    newsHtml: html,
    registry,
    registryError: registry?.error ?? null,
    cachedAt: (data.intel_updated_at as string | undefined) ?? undefined,
    source: 'cache',
  };
}
